import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  FileText,
  FilePdf,
  MagnifyingGlass,
  Printer,
  DownloadSimple,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { printPdf as printPdfUtil, downloadPdf as downloadPdfUtilFn } from '../utils/pdfUtils';
import DatePickerDMY from '../components/DatePickerDMY';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const formatDateDMY = (d) => {
  if (!d) return '';
  const p = d.split('-');
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : d;
};

export default function ApartmentStatement() {
  const { t, language } = useLanguage();
  const [searchParams] = useSearchParams();
  const [buildings, setBuildings] = useState([]);
  const [selectedBuilding, setSelectedBuilding] = useState('');
  const [apartments, setApartments] = useState([]);
  const [selectedApartment, setSelectedApartment] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(false);
  const [autoGenerate, setAutoGenerate] = useState(false);

  useEffect(() => {
    const now = new Date();
    const yearAgo = new Date(now.getFullYear() - 1, now.getMonth(), 1);
    setDateFrom(yearAgo.toISOString().split('T')[0]);
    setDateTo(now.toISOString().split('T')[0]);
    fetchBuildings();
  }, []);

  // Handle URL params for direct linking
  useEffect(() => {
    const buildingParam = searchParams.get('building');
    const apartmentParam = searchParams.get('apartment');
    if (buildingParam && buildings.length > 0) {
      setSelectedBuilding(buildingParam);
      if (apartmentParam) {
        setAutoGenerate(true);
      }
    }
  }, [buildings, searchParams]);

  useEffect(() => {
    if (selectedBuilding) {
      fetchApartments(selectedBuilding);
    } else {
      setApartments([]);
    }
    if (!autoGenerate) {
      setSelectedApartment('');
      setStatement(null);
    }
  }, [selectedBuilding]);

  // Auto-select apartment from URL param after apartments load
  useEffect(() => {
    const apartmentParam = searchParams.get('apartment');
    if (autoGenerate && apartmentParam && apartments.length > 0) {
      const found = apartments.find(a => a.apartment_id === apartmentParam);
      if (found) {
        setSelectedApartment(apartmentParam);
        setAutoGenerate(false);
        // Auto-generate after state updates
        setTimeout(() => generateStatementFor(apartmentParam), 200);
      }
    }
  }, [apartments, autoGenerate]);

  const fetchBuildings = async () => {
    try {
      const res = await axios.get(`${API}/buildings`);
      setBuildings(res.data);
    } catch (e) { console.error(e); }
  };

  const fetchApartments = async (buildingId) => {
    try {
      const res = await axios.get(`${API}/buildings/${buildingId}/apartments`);
      setApartments(res.data);
    } catch (e) { console.error(e); }
  };

  const generateStatementFor = async (aptId) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/apartments/${aptId}/statement`, {
        params: { date_from: dateFrom, date_to: dateTo },
      });
      setStatement(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  };

  const generateStatement = async () => {
    if (!selectedApartment) {
      toast.error(t('selectApartment'));
      return;
    }
    await generateStatementFor(selectedApartment);
  };

  const downloadPdf = () => {
    downloadPdfUtilFn(`/apartments/${selectedApartment}/statement/pdf`, { date_from: dateFrom, date_to: dateTo });
    toast.success(t('exportSuccess'));
  };

  const handlePrintPdf = () => {
    printPdfUtil(`/apartments/${selectedApartment}/statement/pdf`, { date_from: dateFrom, date_to: dateTo });
  };

  const getDescription = (entry) => {
    if (language === 'el' && entry.description_el) return entry.description_el;
    return entry.description;
  };

  let runningBalance = statement ? statement.opening_balance : 0;

  return (
    <Layout>
      <div data-testid="apartment-statement-page">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-zinc-900" style={{ fontFamily: 'var(--font-heading)' }}>
            {t('apartmentStatement')}
          </h1>
        </div>

        {/* Filters */}
        <div className="bg-white border border-zinc-200 p-5 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 items-end">
            <div>
              <Label className="text-xs text-zinc-500">{t('buildings')}</Label>
              <Select value={selectedBuilding} onValueChange={(val) => { setSelectedBuilding(val); setAutoGenerate(false); }}>
                <SelectTrigger className="mt-1 rounded-none" data-testid="stmt-building-select">
                  <SelectValue placeholder={t('buildings')} />
                </SelectTrigger>
                <SelectContent>
                  {buildings.map(b => (
                    <SelectItem key={b.building_id} value={b.building_id}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-zinc-500">{t('apartment')}</Label>
              <Select value={selectedApartment} onValueChange={(val) => { setSelectedApartment(val); setStatement(null); }}>
                <SelectTrigger className="mt-1 rounded-none" data-testid="stmt-apartment-select">
                  <SelectValue placeholder={t('selectApartment')} />
                </SelectTrigger>
                <SelectContent>
                  {apartments.map(a => (
                    <SelectItem key={a.apartment_id} value={a.apartment_id}>
                      {a.apartment_number}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-zinc-500">{t('dateFrom')}</Label>
              <DatePickerDMY value={dateFrom} onChange={setDateFrom} className="mt-1 rounded-none font-mono" data-testid="stmt-date-from" />
            </div>
            <div>
              <Label className="text-xs text-zinc-500">{t('dateTo')}</Label>
              <DatePickerDMY value={dateTo} onChange={setDateTo} className="mt-1 rounded-none font-mono" data-testid="stmt-date-to" />
            </div>
            <div>
              <Button onClick={generateStatement} disabled={loading || !selectedApartment} className="bg-zinc-950 rounded-none w-full" data-testid="generate-statement-btn">
                <MagnifyingGlass size={18} className="mr-2" />
                {loading ? '...' : t('generateStatement')}
              </Button>
            </div>
          </div>
        </div>

        {/* Statement Result */}
        {statement && (
          <div className="bg-white border border-zinc-200" data-testid="statement-result">
            {/* Header */}
            <div className="px-6 py-5 border-b border-zinc-200 flex items-start justify-between">
              <div>
                <h2 className="text-lg font-bold" style={{ fontFamily: 'var(--font-heading)' }}>
                  {statement.building.name} — {t('apartment')} {statement.apartment.apartment_number}
                </h2>
                <p className="text-sm text-zinc-500 mt-1">
                  {t('period')}: {formatDateDMY(statement.date_from)} — {formatDateDMY(statement.date_to)}
                </p>
                {/* Phase 239 — Reminder Sent badge with last reminder metadata. */}
                {statement.last_reminder && (
                  <div className="mt-2 inline-flex items-center gap-2 px-2 py-1 border border-amber-300 bg-amber-50 text-amber-800 text-[11px] font-semibold" data-testid="reminder-sent-badge">
                    <span>⚠ {t('reminderSent') || 'Reminder Sent'}</span>
                    <span className="text-amber-600 font-mono">
                      {formatDateDMY(statement.last_reminder.sent_at?.slice(0, 10))}
                    </span>
                    <span className="text-amber-500 uppercase tracking-wider text-[9px]">
                      {statement.last_reminder.method} · {statement.last_reminder.stage}
                    </span>
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={handlePrintPdf} className="rounded-none" data-testid="print-pdf-btn">
                  <Printer size={18} className="mr-2" />{t('printPdf')}
                </Button>
                <Button variant="outline" onClick={downloadPdf} className="rounded-none" data-testid="download-pdf-btn">
                  <DownloadSimple size={18} className="mr-2" />{t('downloadPdf')}
                </Button>
              </div>
            </div>

            {/* Opening Balance */}
            <div className="px-6 py-3 bg-zinc-50 border-b border-zinc-200 flex justify-between items-start gap-4">
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-sm">
                  {/* Phase 236/240 — Render as "Opening Balance (notes)" so it
                      matches the backend statement entry format. Empty notes
                      → only the label. */}
                  <span>{t('openingBalance')}</span>
                  {statement.apartment?.opening_balance_notes && (
                    <span className="text-zinc-600 font-normal italic ml-1.5" data-testid="opening-balance-note">
                      ({statement.apartment.opening_balance_notes})
                    </span>
                  )}
                </div>
              </div>
              <span className={`font-mono font-bold text-lg shrink-0 ${statement.opening_balance > 0 ? 'text-red-700' : statement.opening_balance < 0 ? 'text-emerald-700' : 'text-zinc-700'}`} data-testid="opening-balance">
                &euro;{statement.opening_balance.toFixed(2)}
              </span>
            </div>

            {/* Transaction Table */}
            {statement.entries.length === 0 ? (
              <div className="p-12 text-center text-zinc-500">{t('noTransactions')}</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="excel-table" data-testid="statement-table">
                  <thead>
                    <tr>
                      <th className="text-xs">{t('expenseDate')}</th>
                      <th className="text-xs">{t('reference')}</th>
                      <th className="text-xs">{t('description')}</th>
                      <th className="text-xs text-right">{t('charge')}</th>
                      <th className="text-xs text-right">{t('payment')}</th>
                      <th className="text-xs text-right">{t('balance')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      let rb = statement.opening_balance;
                      return statement.entries.map((entry, idx) => {
                        if (entry.type === 'charge') rb += entry.amount;
                        else rb -= entry.amount;
                        return (
                          <tr key={idx} data-testid={`statement-entry-${idx}`}>
                            <td className="font-mono text-sm">{formatDateDMY(entry.date)}</td>
                            <td className="font-mono text-sm text-zinc-700" data-testid={`statement-ref-${idx}`}>{entry.reference || ''}</td>
                            <td className="text-sm">{getDescription(entry)}</td>
                            <td className="font-mono text-sm text-right text-red-700">
                              {entry.type === 'charge' ? `€${entry.amount.toFixed(2)}` : ''}
                            </td>
                            <td className="font-mono text-sm text-right text-emerald-700">
                              {entry.type === 'payment' ? `€${entry.amount.toFixed(2)}` : ''}
                            </td>
                            <td className={`font-mono text-sm text-right font-semibold ${rb > 0 ? 'text-red-700' : 'text-emerald-700'}`}>
                              &euro;{rb.toFixed(2)}
                            </td>
                          </tr>
                        );
                      });
                    })()}
                  </tbody>
                  <tfoot>
                    <tr className="bg-zinc-50 font-semibold border-t-2 border-zinc-300">
                      <td colSpan={3} className="text-right text-sm">{t('total')}</td>
                      <td className="font-mono text-right text-red-700">&euro;{statement.total_charges.toFixed(2)}</td>
                      <td className="font-mono text-right text-emerald-700">&euro;{statement.total_payments.toFixed(2)}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}

            {/* Closing Balance */}
            <div className="px-6 py-4 bg-zinc-50 border-t border-zinc-200 flex justify-between items-center">
              <span className="font-bold">{t('closingBalance')}</span>
              <span className={`font-mono font-bold text-xl ${statement.closing_balance > 0 ? 'text-red-700' : statement.closing_balance < 0 ? 'text-emerald-700' : 'text-zinc-700'}`} data-testid="closing-balance">
                &euro;{statement.closing_balance.toFixed(2)}
              </span>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-3 border-t border-zinc-200">
              <div className="px-6 py-4 text-center border-r border-zinc-200">
                <p className="text-xs text-zinc-500 mb-1">{t('totalCharges')}</p>
                <p className="font-mono font-bold text-red-700" data-testid="total-charges">&euro;{statement.total_charges.toFixed(2)}</p>
              </div>
              <div className="px-6 py-4 text-center border-r border-zinc-200">
                <p className="text-xs text-zinc-500 mb-1">{t('totalPayments')}</p>
                <p className="font-mono font-bold text-emerald-700" data-testid="total-payments">&euro;{statement.total_payments.toFixed(2)}</p>
              </div>
              <div className="px-6 py-4 text-center">
                <p className="text-xs text-zinc-500 mb-1">{t('closingBalance')}</p>
                <p className={`font-mono font-bold ${statement.closing_balance > 0 ? 'text-red-700' : 'text-emerald-700'}`}>&euro;{statement.closing_balance.toFixed(2)}</p>
              </div>
            </div>
          </div>
        )}

        {/* Empty state */}
        {!statement && selectedApartment && (
          <div className="bg-white border border-zinc-200 p-12 text-center">
            <FileText size={48} className="mx-auto mb-3 text-zinc-300" />
            <p className="text-zinc-500">{t('generateStatement')}</p>
          </div>
        )}
      </div>
    </Layout>
  );
}
